import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useLocation } from 'wouter';
import { ChevronLeft, Check } from 'lucide-react';
import { ChartEditor } from '../components/ChartEditor';
import { api } from '../lib/api';
import type { ChartElement, CanvasJSON, ChartMetadata } from '@shared/types';

type HighlightKind = 'changed' | 'new';

function detectChanges(
  baseEls: ChartElement[],
  personalEls: ChartElement[],
): Map<string, HighlightKind> {
  const highlights = new Map<string, HighlightKind>();
  const personalById = new Map(personalEls.map(el => [el.id, el]));
  const matchedPersonalIds = new Set<string>();

  const personalByType = new Map<string, ChartElement[]>();
  for (const el of personalEls) {
    if (!personalByType.has(el.type)) personalByType.set(el.type, []);
    personalByType.get(el.type)!.push(el);
  }
  const typeMatchCursor = new Map<string, number>();

  for (const baseEl of baseEls) {
    let match: ChartElement | undefined;

    const direct = personalById.get(baseEl.id);
    if (direct && !matchedPersonalIds.has(direct.id)) {
      match = direct;
    }

    if (!match) {
      const list = personalByType.get(baseEl.type) || [];
      const cursor = typeMatchCursor.get(baseEl.type) || 0;
      for (let i = cursor; i < list.length; i++) {
        if (!matchedPersonalIds.has(list[i].id)) {
          match = list[i];
          typeMatchCursor.set(baseEl.type, i + 1);
          break;
        }
      }
    }

    if (!match) {
      highlights.set(baseEl.id, 'new');
    } else {
      matchedPersonalIds.add(match.id);
      if (JSON.stringify(baseEl.content) !== JSON.stringify(match.content)) {
        highlights.set(baseEl.id, 'changed');
      }
    }
  }

  return highlights;
}

export function CompareView() {
  const params = useParams<{ personalId: string }>();
  const personalId = Number(params.personalId);
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'base' | 'my'>('base');

  const { data: personal, isLoading: personalLoading } = useQuery({
    queryKey: ['personalChart', personalId],
    queryFn: () => api.charts.getPersonalDetail(personalId),
  });

  const { data: base, isLoading: baseLoading } = useQuery({
    queryKey: ['baseChart', personal?.baseChartId],
    queryFn: () => api.charts.getBase(personal!.baseChartId),
    enabled: !!personal?.baseChartId,
  });

  const { data: song } = useQuery({
    queryKey: ['song', personal?.songId],
    queryFn: () => api.songs.get(personal!.songId),
    enabled: !!personal?.songId,
  });

  const backUrl = personal?.bandId && personal?.songId
    ? `/bands/${personal.bandId}/songs/${personal.songId}`
    : '/';

  const markReviewed = useMutation({
    mutationFn: () => api.charts.acknowledgeUpdate(personalId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personalChart', personalId] });
      navigate(backUrl);
    },
  });

  const handlePersonalSave = async (
    canvas: CanvasJSON,
    metadata: ChartMetadata | Partial<ChartMetadata>,
    createVersion = false,
  ) => {
    await api.charts.updatePersonal(
      personalId,
      { chartMetadataOverride: metadata, canvasJson: canvas },
      createVersion,
    );
    queryClient.invalidateQueries({ queryKey: ['personalChart', personalId] });
  };

  if (personalLoading || baseLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-surface-950 text-gray-400">
        Loading charts...
      </div>
    );
  }

  if (!personal || !base) {
    return (
      <div className="h-full flex items-center justify-center bg-surface-950 text-gray-400">
        Charts not found
      </div>
    );
  }

  const highlights = detectChanges(
    base.canvasJson?.elements || [],
    personal.canvasJson?.elements || [],
  );

  const changedCount = [...highlights.values()].filter(v => v === 'changed').length;
  const newCount = [...highlights.values()].filter(v => v === 'new').length;

  return (
    <div className="h-full flex flex-col bg-surface-950">
      {/* Header */}
      <div className="bg-surface-900 border-b border-surface-700 px-3 py-2 flex items-center gap-2 shrink-0">
        <button
          onClick={() => navigate(backUrl)}
          className="p-1 hover:bg-surface-800 rounded transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-gray-400" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">
            {song?.title || 'Chart'} — Compare
          </p>
          <p className="text-xs text-gray-500">
            Base v{base.versionNumber} vs My Version v{personal.versionNumber}
            {(changedCount + newCount) > 0 && (
              <span className="ml-2">
                {changedCount > 0 && <span className="text-amber-400">{changedCount} updated</span>}
                {changedCount > 0 && newCount > 0 && <span className="text-gray-600 mx-1">·</span>}
                {newCount > 0 && <span className="text-green-400">{newCount} new</span>}
              </span>
            )}
            {changedCount === 0 && newCount === 0 && (
              <span className="ml-2 text-gray-600">No content changes detected</span>
            )}
          </p>
        </div>
        <button
          onClick={() => markReviewed.mutate()}
          disabled={markReviewed.isPending}
          className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors"
        >
          <Check className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">{markReviewed.isPending ? 'Marking...' : 'Mark Reviewed'}</span>
          <span className="sm:hidden">Done</span>
        </button>
      </div>

      {/* Legend + helper text */}
      <div className="bg-surface-950 border-b border-surface-800 px-4 py-2 flex items-center gap-2 shrink-0 flex-wrap">
        <p className="text-xs text-gray-500 flex-1 min-w-0">
          Base is read-only. Edit your My Version manually if you want to incorporate changes.
        </p>
        <div className="flex items-center gap-3 shrink-0">
          <span className="flex items-center gap-1 text-[11px] text-amber-400">
            <span className="inline-block w-3 h-3 rounded-sm border-2 border-amber-400" />
            Updated
          </span>
          <span className="flex items-center gap-1 text-[11px] text-green-400">
            <span className="inline-block w-3 h-3 rounded-sm border-2 border-green-500" />
            New in Base
          </span>
        </div>
      </div>

      {/* Mobile tab switcher */}
      <div className="flex md:hidden border-b border-surface-700 shrink-0 bg-surface-900">
        <button
          onClick={() => setActiveTab('base')}
          className={`flex-1 py-2.5 text-sm font-medium transition-colors ${activeTab === 'base' ? 'text-white border-b-2 border-primary-500' : 'text-gray-500 hover:text-gray-300'}`}
        >
          Base Chart
        </button>
        <button
          onClick={() => setActiveTab('my')}
          className={`flex-1 py-2.5 text-sm font-medium transition-colors ${activeTab === 'my' ? 'text-white border-b-2 border-accent-500' : 'text-gray-500 hover:text-gray-300'}`}
        >
          My Version
        </button>
      </div>

      {/* Two-pane content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Base Chart pane */}
        <div className={`flex-1 flex flex-col overflow-hidden ${activeTab !== 'base' ? 'hidden md:flex' : 'flex'}`}>
          <div className="bg-surface-900 border-b border-surface-700 px-3 py-1.5 flex items-center gap-2 shrink-0">
            <span className="text-xs font-semibold text-gray-300">Base Chart</span>
            <span className="text-xs text-gray-500">v{base.versionNumber}</span>
            <span className="text-xs bg-surface-700 text-gray-500 px-1.5 py-0.5 rounded ml-auto">Read Only</span>
          </div>
          <div className="flex-1 overflow-hidden">
            <ChartEditor
              key={`compare-base-${base.id}`}
              chartType="base"
              chartId={base.id}
              songId={base.songId}
              initialCanvas={base.canvasJson || { elements: [], canvasWidth: 800, canvasHeight: 1100, zoom: 1 }}
              initialMetadata={base.chartMetadata || {}}
              readOnly
              elementHighlights={highlights}
            />
          </div>
        </div>

        {/* Vertical divider — desktop only */}
        <div className="hidden md:block w-px bg-surface-700 shrink-0" />

        {/* My Version pane */}
        <div className={`flex-1 flex flex-col overflow-hidden ${activeTab !== 'my' ? 'hidden md:flex' : 'flex'}`}>
          <div className="bg-surface-900 border-b border-surface-700 px-3 py-1.5 flex items-center gap-2 shrink-0">
            <span className="text-xs font-semibold text-accent-400">My Version</span>
            <span className="text-xs text-gray-500">v{personal.versionNumber}</span>
          </div>
          <div className="flex-1 overflow-hidden">
            <ChartEditor
              key={`compare-personal-${personal.id}`}
              chartType="personal"
              chartId={personal.id}
              songId={personal.songId}
              initialCanvas={personal.canvasJson || { elements: [], canvasWidth: 800, canvasHeight: 1100, zoom: 1 }}
              initialMetadata={personal.chartMetadataOverride || {}}
              onSave={handlePersonalSave}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
